import { useRemoteClientPost, useRemoteClientPut } from '@/configs/http-client'
import { Endpoints } from '@/remote/endpoints'
import { useLocalStorage } from '@/remote/local-storage'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { ComboSelectWithLabel } from '../components/combo-select-with-label'
import { Button } from '../components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form'
import { Input } from '../components/ui/input'
import { useToast } from '../hooks'
import { ExerciseFormData, exerciseSchema } from '../validators/exercise-validator'

interface ExerciseFormProps {
  data?: ExerciseFormData & { id?: number }
  closeDialog?: () => void
  updateList?: () => void
}

export default function ExerciseForm({ data, closeDialog, updateList }: ExerciseFormProps) {
  const { httpClientPost } = useRemoteClientPost()
  const { httpClientPut } = useRemoteClientPut()
  const { getUnitId } = useLocalStorage()
  const { showToast } = useToast()
  const [params] = useSearchParams()
  const isEditMode = Boolean(data?.id)
  const [gruops, setGroups] = useState<any>([])

  const form = useForm<ExerciseFormData>({
    resolver: zodResolver(exerciseSchema),
    defaultValues: {
      id: data?.id || 0,
      nome: data?.nome || '',
      descricao: data?.descricao || '',
      idGrupo: data?.idGrupo || 0,
      unidade: {
        id: data?.unidade?.id || params.get('unit') || getUnitId()
      }
    }
  })

  const onSubmit = async (formData: ExerciseFormData) => {
    const formattedData = { ...formData }

    try {
      if (isEditMode) {
        await httpClientPut(Endpoints.exercise, { ...formattedData, inclusao: new Date() })
        closeDialog?.()
        updateList?.()
      } else {
        delete formattedData.id
        await httpClientPost(Endpoints.exercise, { ...formattedData })
        closeDialog?.()
        updateList?.()
      }

      showToast('Requisição enviada com sucesso!', 'success')
      form.reset()
    } catch (error: any) {
      console.error('Erro no envio:', error?.response?.data?.errors)
      const errors = error?.response?.data?.errors
      if (errors) {
        for (const key in errors) {
          showToast(errors[key], 'error')
        }
      }
      showToast('Erro ao enviar requisição', 'error')
    }
  }

  const unidadeId = Number(form.watch('unidade.id'))

  const fetchGroups = async (unidadeId: number) => {
    setGroups([])

    try {
      const response = await httpClientPost(`${Endpoints.gruops}/filtro?page=${0}&size=${100}&order=desc&sort=id`, {
        idUnidade: unidadeId
      })

      const data = response?.data?.data?.content || []
      setGroups(
        data.map((elem: any) => ({
          ...elem,
          label: elem.nome,
          value: elem.id
        }))
      )
      console.log(data)
    } catch (error) {
      console.error('Erro ao buscar dados de serviços:', error)
    }
  }

  const formValues = form.watch()

  useEffect(() => {
    console.log(formValues)
  }, [formValues])

  const {
    formState: { errors }
  } = form

  useEffect(() => {
    console.log('Erros do formulário:', errors)
  }, [errors])

  useEffect(() => {
    fetchGroups(params.get('unit') || getUnitId())
  }, [unidadeId, params])

  return (
    <Form {...form}>
      <form id="formExercise" onSubmit={form.handleSubmit(onSubmit)} className="max-w-128 p-4 w-full flex flex-col m-auto">
        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="nome"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span>
                  Nome
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Nome" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.nome?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="descricao"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.descricao?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="idGrupo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Grupo Muscular</FormLabel>
                <FormControl>
                  <ComboSelectWithLabel
                    defaultValue={field.value}
                    data={gruops}
                    onChange={(e) => {
                      console.log(e)
                      const id = e?.value
                      form.setValue('idGrupo', id)
                      form.trigger(['idGrupo'])
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.idGrupo?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>

        {!isEditMode && (
          <Button type="submit" className="bg-acelerafit-green-400 text-muted  font-bold hover:bg-acelerafit-green-300 mt-3 transition-all">
            Enviar
          </Button>
        )}
      </form>
    </Form>
  )
}
